import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Island, Office, Page, ServiceType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const ISLANDS: Island[] = ['MAHE', 'PRASLIN', 'LA_DIGUE', 'SILHOUETTE', 'OTHER'];
const DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const TABS = [
  { key: 'offices', label: 'Offices' },
  { key: 'services', label: 'Service types' },
] as const;

export function Catalogue() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('OFFICE_MANAGER') ?? false;
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('offices');
  const [showCreateOffice, setShowCreateOffice] = useState(false);
  const [showCreateService, setShowCreateService] = useState(false);

  const officesQ = useApiQuery<Page<Office>>('/api/v1/servicedesk/offices?limit=100');
  const serviceTypesQ = useApiQuery<Page<ServiceType>>('/api/v1/servicedesk/service-types?limit=100');

  return (
    <div>
      <PageHeader
        title="Offices & Services"
        subtitle="Catalogue admin — c10-servicedesk only exposes create + list for these; there is no edit/delete endpoint yet."
        actions={
          canManage ? (
            tab === 'offices' ? (
              <button className="btn-primary" onClick={() => setShowCreateOffice(true)}>
                + New office
              </button>
            ) : (
              <button className="btn-primary" onClick={() => setShowCreateService(true)}>
                + New service type
              </button>
            )
          ) : undefined
        }
      />

      <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Creating offices or service types requires the Office Manager role — the lists below are visible to everyone." />
        </div>
      ) : null}

      {tab === 'offices' ? (
        <>
          {officesQ.error ? <ErrorNote message={officesQ.error} /> : null}
          <div className="card overflow-x-auto">
            {officesQ.loading ? (
              <Spinner />
            ) : (officesQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No offices yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Code</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">Island</th>
                    <th className="table-head">District</th>
                    <th className="table-head text-right">Counters</th>
                    <th className="table-head">Services</th>
                  </tr>
                </thead>
                <tbody>
                  {officesQ.data!.items.map((o) => (
                    <tr key={o.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{o.code}</td>
                      <td className="table-cell">{o.name}</td>
                      <td className="table-cell">
                        <StatusBadge status={o.island} />
                      </td>
                      <td className="table-cell">{o.district}</td>
                      <td className="table-cell text-right tabular-nums">{o.counters}</td>
                      <td className="table-cell text-xs text-slate-500">{o.services.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          {serviceTypesQ.error ? <ErrorNote message={serviceTypesQ.error} /> : null}
          <div className="card overflow-x-auto">
            {serviceTypesQ.loading ? (
              <Spinner />
            ) : (serviceTypesQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No service types yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Code</th>
                    <th className="table-head">Module</th>
                    <th className="table-head text-right">Duration</th>
                    <th className="table-head">Required documents</th>
                    <th className="table-head">Bookable online</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceTypesQ.data!.items.map((st) => (
                    <tr key={st.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{st.code}</td>
                      <td className="table-cell">{st.moduleCode}</td>
                      <td className="table-cell text-right tabular-nums">{st.durationMin} min</td>
                      <td className="table-cell text-xs text-slate-500">{st.requiredDocs.join(', ') || '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={st.bookableOnline ? 'ACTIVE' : 'CLOSED'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {showCreateOffice ? (
        <CreateOfficeModal
          serviceTypes={serviceTypesQ.data?.items ?? []}
          onClose={() => setShowCreateOffice(false)}
          onDone={() => {
            setShowCreateOffice(false);
            officesQ.reload();
          }}
        />
      ) : null}
      {showCreateService ? (
        <CreateServiceTypeModal
          onClose={() => setShowCreateService(false)}
          onDone={() => {
            setShowCreateService(false);
            serviceTypesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateOfficeModal({
  serviceTypes,
  onClose,
  onDone,
}: {
  serviceTypes: ServiceType[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [island, setIsland] = useState<Island>('MAHE');
  const [district, setDistrict] = useState('');
  const [lng, setLng] = useState('55.45');
  const [lat, setLat] = useState('-4.62');
  const [counters, setCounters] = useState('1');
  const [services, setServices] = useState<string[]>([]);
  const [hours, setHours] = useState<Record<string, string>>({
    mon: '08:00-16:00',
    tue: '08:00-16:00',
    wed: '08:00-16:00',
    thu: '08:00-16:00',
    fri: '08:00-13:00',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggleService = (svcCode: string) => {
    setServices((s) => (s.includes(svcCode) ? s.filter((c) => c !== svcCode) : [...s, svcCode]));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const hoursPayload = Object.fromEntries(Object.entries(hours).filter(([, v]) => v.trim().length > 0));
      await api.post('/api/v1/servicedesk/offices', {
        code,
        name,
        island,
        district,
        geo: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        services,
        hours: hoursPayload,
        counters: Number.parseInt(counters, 10),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create office');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New office" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. GRAND_ANSE_MAHE" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grand Anse Mahé District Office" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Island">
            <select className="field-input" value={island} onChange={(e) => setIsland(e.target.value as Island)}>
              {ISLANDS.map((i) => (
                <option key={i} value={i}>
                  {i.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Grand Anse Mahé" />
          </Field>
          <Field label="Counters">
            <input className="field-input" type="number" min="0" max="100" value={counters} onChange={(e) => setCounters(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Longitude">
            <input className="field-input" type="number" step="0.0001" value={lng} onChange={(e) => setLng(e.target.value)} />
          </Field>
          <Field label="Latitude">
            <input className="field-input" type="number" step="0.0001" value={lat} onChange={(e) => setLat(e.target.value)} />
          </Field>
        </div>

        <Field label="Opening hours (blank = closed)">
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
            {DAYS.map((d) => (
              <div key={d.key}>
                <div className="mb-0.5 text-center text-[10px] font-semibold uppercase text-slate-400">{d.label}</div>
                <input
                  className="field-input !px-1.5 !py-1 text-center text-xs"
                  value={hours[d.key] ?? ''}
                  onChange={(e) => setHours((h) => ({ ...h, [d.key]: e.target.value }))}
                  placeholder="closed"
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Format HH:MM-HH:MM, e.g. 08:00-16:00.</p>
        </Field>

        <Field label="Services offered">
          {serviceTypes.length === 0 ? (
            <p className="text-xs text-slate-400">No service types exist yet — create one first.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {serviceTypes.map((st) => (
                <label
                  key={st.code}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                    services.includes(st.code) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                  }`}
                >
                  <input type="checkbox" className="hidden" checked={services.includes(st.code)} onChange={() => toggleService(st.code)} />
                  {st.code}
                </label>
              ))}
            </div>
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || code.trim().length < 2 || name.trim().length < 2 || district.trim().length < 2}
          >
            {busy ? 'Creating…' : 'Create office'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CreateServiceTypeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [moduleCode, setModuleCode] = useState('');
  const [durationMin, setDurationMin] = useState('15');
  const [requiredDocs, setRequiredDocs] = useState('');
  const [bookableOnline, setBookableOnline] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/servicedesk/service-types', {
        code,
        moduleCode,
        durationMin: Number.parseInt(durationMin, 10),
        requiredDocs: requiredDocs
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        bookableOnline,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create service type');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New service type" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Code">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. BOAT_LICENCE_RENEWAL" />
        </Field>
        <Field label="Owning module code">
          <input className="field-input" value={moduleCode} onChange={(e) => setModuleCode(e.target.value.toUpperCase())} placeholder="e.g. SLTA" />
        </Field>
        <Field label="Duration (minutes)">
          <input className="field-input" type="number" min="1" max="480" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} />
        </Field>
        <Field label="Required documents (comma separated)">
          <input className="field-input" value={requiredDocs} onChange={(e) => setRequiredDocs(e.target.value)} placeholder="e.g. Old licence, Medical certificate" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={bookableOnline} onChange={(e) => setBookableOnline(e.target.checked)} />
          Bookable online (otherwise walk-in only)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || code.trim().length < 2 || moduleCode.trim().length < 1}>
            {busy ? 'Creating…' : 'Create service type'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
